<script setup lang="ts">
import Permissions from '#layers/aicore/shared/permissions';
import type { Content } from '#layers/core/app/runtime/services/StorageService';
import AiImage from '#layers/aicore/app/assets/ai/ai-image.png';

const content = defineModel<Record<string, Content>>({});
const props = defineProps<{
    resource: FileResource & { publicId?: string };
    loading: boolean;
}>();

const emit = defineEmits<{
    (e: 'save'): void;
}>();

const { $log } = useLogging();
const { $alert } = useAlerts();
const aiStore = useAiSettingsStore();
const { $permissions } = usePermissions();

const generating = ref(false);
const imageMetadataGeneratorCanAccess = ref<boolean>(false);

onMounted(async (): Promise<void> =>
{
    imageMetadataGeneratorCanAccess.value = await $permissions.all([Permissions.AiCore.ImageMetadataGenerator.Access]);
});

const aiEnabled = computed(() => aiStore.aiEnabled && imageMetadataGeneratorCanAccess.value);

async function getDescriptImage()
{
    try
    {
        generating.value = true;
        $alert.info($t('[[[Trwa generowanie opisu dla wybranego obrazu]]]'));

        const model = aiStore.getDefaultModelByType(AiModelTypes.VISION);

        const imageMetadata = await AiCoreImageMetadataService.genImageMetadata(
            {
                provider: model.aiProviderName,
                owner: model.owner,
                name: model.name
            },
            {
                imagePublicId: props.resource.publicId
            }
        );

        content.value = imageMetadata;

        $alert.success($t('[[[Obraz został pomyślnie opisany]]]'));
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning($t(ex.message)),
            0: (ex: any) => $alert.error($t('[[[Wystąpił problem podczas przesyłania obrazu.]]]'))
        });
    }
    finally
    {
        generating.value = false;
    }
}

function onSave()
{
    emit('save');
}
</script>

<template>
    <IdeoButton
        type="submit"
        :loading="props.loading"
        :disabled="generating"
        variant="primary"
        class="text-nowrap"
        @click.prevent="onSave"
    >
        <i class="fas fa-check" /> {{ $t('[[[Zapisz]]]') }}
    </IdeoButton>

    <template v-if="!aiEnabled">
        <div id="ai-generator-button" class="me-1">
            <ActionButton disabled variant="fancy" icon="far fa-microchip-ai" :text="$t('[[[Generuj metadane]]]')" @click.prevent />
        </div>

        <IdeoPopover
            class="popover popover-ai-hint"
            placement="top"
            target="ai-generator-button"
            triggers="hover"
            :shadow="false"
            :rounded="true"
        >
            <div>
                <img :src="AiImage" alt="...">

                <div class="p-3">
                    <div class="fs-5 mb-1 fw-bolder">
                        {{ $t('[[[Wybróbuj możliwości AI]]]') }}
                    </div>

                    <div class="fs-6 mb-3">
                        {{ $t('[[[Przejdź do konfiguracji, aby włączyć narzędzia]]]') }}
                        <b>{{ $t('[[[Asystenta AI]]]') }}</b>.
                    </div>

                    <ActionButton variant="fancy" icon="far fa-microchip-ai" :text="$t('[[[Konfiguracja asystenta ai]]]')" :to="{ name: 'ai-settings' }" />
                </div>
            </div>
        </IdeoPopover>
    </template>
    <template v-else>
        <div id="ai-generator-button" class="me-1">
            <ActionButton
                :loading="generating"
                :disabled="props.loading"
                variant="fancy"
                icon="far fa-microchip-ai"
                :text="$t('[[[Generuj metadane]]]')"
                @click.prevent="getDescriptImage()"
            />
        </div>
    </template>
</template>
